import React from 'react';
import {
  Dialog,
  Button,
  FormControl,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import FormInput from 'components/FormInput';
import moment from 'moment';
import { FormLabel } from 'react-bootstrap';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { IIncidents } from 'redux/services/incident';
import './StartIncidentModal.scss';
import { userRoles } from 'constants/constants';
import { WorkFlowDate } from 'components/common/WorkFlowDate';

type StartIncidentModalProps = {
  open: boolean;
  incident: IIncidents;
  onClose: () => void;
};

export const StartIncidentModal: React.FC<StartIncidentModalProps> = ({
  open,
  incident,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const project = useSelector((state: RootStates) =>
    state.project.projects.find((item) => item.projectInfo.id === projectId),
  );
  const subProjects = Object.values({
    subProject: incident.subProject?.name ?? '',
    subTask: incident.subTask?.name ?? '',
    endTask: incident.endTask?.name ?? '',
  })
    .filter((value) => value)
    .join(' -> ');

  const handleSubmit = () => {
    if (!incident.incidentType) {
      navigate(`/project/${projectId}/record-incident/${incident._id}/1`);
    } else {
      navigate(`/project/${projectId}/incident/${incident._id}/review`);
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getTitle = () => {
    if (!incident.incidentType) {
      return t('modal_title_complete_incident');
    }

    return t('modal_title_review_incident');
  };

  const getStartButtonLabel = () => {
    if (!incident.incidentType) {
      return t('btn_continue');
    }

    return t('btn_review');
  };

  const renderDate = () => {
    if (incident?.status === WorkFlowStatus.DRAFT) {
      return (
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_draft_date')}>{`${t(
            'label_draft_date',
          )}: `}</FormLabel>
          <b className="px-3">
            {moment(incident.updated_at || incident.created_at).format(
              'DD/MM/YYYY',
            )}
          </b>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column">
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_incident_start_date')}>{`${t(
            'label_incident_start_date',
          )}: `}</FormLabel>
          <b className="px-3">
            {moment(incident?.incidentDate || incident?.updated_at).format(
              'DD/MM/YYYY',
            )}
          </b>
        </div>
        {incident?.status === WorkFlowStatus.CLOSE && (
          <div className="d-flex flex-nowrap justify-content-between">
            <FormLabel name={t('label_incident_close_date')}>{`${t(
              'label_incident_close_date',
            )}: `}</FormLabel>
            <b className="px-3">
              {moment(incident?.updated_at).format('DD/MM/YYYY')}
            </b>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="start-inspection-modal"
      PaperProps={{
        style: {
          padding: 0,
          minWidth: 300,
        },
      }}
    >
      <DialogActions className="dialog-actions d-flex justify-content-between w-100">
        <div className="d-flex flex-wrap align-items-center">
          <DialogTitle className="p-1" color="inherit">
            {getTitle()}
          </DialogTitle>
          <div className="flex-grow-1 align-items-start">
            <span className={`status ${incident?.status?.toLowerCase()}`}>
              {incident.status}
            </span>
          </div>
        </div>
        <IconButton className="close-btn" onClick={onClose}>
          <Close color="inherit" />
        </IconButton>
      </DialogActions>
      <FormControl className="p-4 d-flex flex-column overflow-auto">
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('incident_id')}>{`${t(
            'incident_id',
          )}:`}</FormLabel>
          <b className="px-3">{incident?.displayId}</b>
        </div>
        {renderDate()}

        {incident?.rcaRequired && (
          <WorkFlowDate
            date={incident.rcaDate!}
            status={incident.status ?? WorkFlowStatus.OPEN}
          />
        )}

        {(incident.locations?.length ?? 0) > 0 && (
          <FormInput
            name={t('label_locations')}
            label={t('label_locations')}
            required={false}
            value={incident.locations?.join(', ')}
            disabled
          />
        )}
        {subProjects.length && (
          <FormInput
            name={t('label_sub_projects')}
            label={t('label_sub_projects')}
            required={false}
            value={subProjects}
            disabled
          />
        )}
        <div className="d-flex flex-nowrap">
          <FormInput
            name={t('label_doer')}
            label={t('label_doer')}
            required={false}
            value={
              project?.team?.find((teamObj) => teamObj.userId === incident.doer)
                ?.name ?? ''
            }
            disabled
          />
          <FormInput
            name={t('label_inspector')}
            label={t('label_inspector')}
            className="ml-1"
            required={false}
            value={
              project?.team?.find(
                (teamObj) => teamObj.userId === incident.inspector,
              )?.name ?? ''
            }
            disabled
          />
        </div>
      </FormControl>
      <footer className="p-4 d-flex flex-row justify-content-between flex-grow-1 align-items-end">
        <Button variant="outlined" onClick={onClose}>
          {t('back')}
        </Button>
        <Button
          hidden={
            project?.role === userRoles.DOER &&
            incident.status === WorkFlowStatus.DRAFT
          }
          variant="contained"
          onClick={handleSubmit}
        >
          {getStartButtonLabel()}
        </Button>
      </footer>
    </Dialog>
  );
};
